/**
 * SCHEDULE — the pure half (migration 3071; DESIGN-CHANGELOG 2026-10-14
 * "SCHEDULE ships into both mirrors"). Ported from the `sc*` family in
 * `design/gt-logic.js`; every function takes its clock explicitly so the
 * view model is testable at any hour.
 *
 * A NIGHT is a calendar date key `YYYY-MM-DD` read off the local clock, never
 * converted — a night is not an instant. A NOMINATION carries an optional
 * window on the night's hour axis, stored the way the server stores it: the
 * hour of the night clock, 9..24, where 24 is LATE (midnight and after), and
 * `null` for an open end.
 */

/** The hour axis, 9A → LATE (the design's `SC_HRS`). Index i is hour 9 + i. */
export const SC_HRS = ['9A', '10A', '11A', 'NOON', '1P', '2P', '3P', '4P', '5P', '6P', '7P', '8P', '9P', '10P', '11P', 'LATE'] as const;
/** The hour the axis opens on. */
export const SC_FIRST_HOUR = 9;
/** The last index on the axis — LATE. */
export const SC_NH = SC_HRS.length - 1;

export const SC_DOW = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;
export const SC_MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'] as const;
export const SC_MONTH = [
  'JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE',
  'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER',
] as const;

/** One nomination as `schedule_night_json()` reports it. */
export interface SchedNom {
  /** appid */
  id: number;
  /** the nominator's user key ('' once their profile is gone) */
  by: string;
  /** epoch ms */
  at: number;
  /** window start, hour of the night clock 9..24; null = open */
  from: number | null;
  /** window end, hour of the night clock 9..24; null = open */
  to: number | null;
  /** every backer's user key, in the order they backed — the nominator first */
  bys: string[];
}

/** Nights by key. An empty night is absent, never `[]`. */
export type SchedMap = Record<string, SchedNom[]>;

/** Hour 9..24 → its index on the axis. */
export function hourIdx(h: number): number {
  return Math.max(0, Math.min(SC_NH, h - SC_FIRST_HOUR));
}

/** Index on the axis → hour 9..24. */
export function idxHour(i: number): number {
  return i + SC_FIRST_HOUR;
}

/** Hour 9..24 → `9A` … `LATE`. */
export function hourLabel(h: number): string {
  return SC_HRS[hourIdx(h)] ?? '';
}

/** A window's start index: an open FROM is the start of the axis. */
export function fromIdx(n: { from: number | null }): number {
  return n.from == null ? 0 : hourIdx(n.from);
}

/** A window's end index: an open UNTIL is the end of the axis. */
export function toIdx(n: { to: number | null }): number {
  return n.to == null ? SC_NH : hourIdx(n.to);
}

/**
 * A clock → a fractional index on the axis. Before 9A clamps to the start,
 * anything past midnight's LATE clamps to the end (the design's `scNowI`).
 */
export function scNowI(d: Date): number {
  const h = d.getHours() + d.getMinutes() / 60;
  return h < SC_FIRST_HOUR ? 0 : Math.min(SC_NH, h - SC_FIRST_HOUR);
}

/** `ANY TIME` · `FROM 6P` · `UNTIL 10P` · `6P–10P` (the design's `scRangeTxt`). */
export function scRangeTxt(from: number | null, to: number | null): string {
  if (from == null && to == null) return 'ANY TIME';
  if (from != null && to == null) return 'FROM ' + hourLabel(from);
  if (from == null && to != null) return 'UNTIL ' + hourLabel(to);
  return hourLabel(from as number) + '–' + hourLabel(to as number);
}

/**
 * Does the window cover axis index `i`? An open end never closes — so a
 * nomination with no UNTIL still covers a NOW clamped to LATE.
 */
export function scFits(n: { from: number | null; to: number | null }, i: number): boolean {
  const f = n.from == null ? 0 : hourIdx(n.from);
  const t = n.to == null ? Infinity : hourIdx(n.to);
  return i >= f && i < t;
}

/** A local date → its night key. */
export function scKey(d: Date): string {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

/** A night key → local midnight of that date. */
export function scParse(k: string): Date {
  const p = k.split('-').map(Number);
  return new Date(p[0] ?? 1970, (p[1] ?? 1) - 1, p[2] ?? 1);
}

/** `FRI · OCT 16` */
export function scDayTxt(k: string): string {
  const d = scParse(k);
  return SC_DOW[d.getDay()] + ' · ' + SC_MON[d.getMonth()] + ' ' + d.getDate();
}

export interface SchedCell {
  /** night key */
  k: string;
  /** day of month */
  n: number;
  inMonth: boolean;
  /** FRI, SAT, SUN — the outlined weekend nights */
  we: boolean;
}

/**
 * Monday-first weeks covering month `m` (0-based) of `y`. Stops after the week
 * that finishes the month — four to six rows (the design's `scMonth`).
 */
export function scMonth(y: number, m: number): SchedCell[][] {
  const first = new Date(y, m, 1);
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(y, m, 1 - lead);
  const out: SchedCell[][] = [];
  for (let w = 0; w < 6; w++) {
    const row: SchedCell[] = [];
    for (let c = 0; c < 7; c++) {
      const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + w * 7 + c);
      row.push({ k: scKey(d), n: d.getDate(), inMonth: d.getMonth() === m, we: c >= 4 });
    }
    out.push(row);
    if (new Date(start.getFullYear(), start.getMonth(), start.getDate() + (w + 1) * 7).getMonth() !== m && w >= 3) break;
  }
  return out;
}

/** The first and last night keys the month grid draws — what to fetch. */
export function scMonthSpan(y: number, m: number): { from: string; to: string } {
  const g = scMonth(y, m);
  const last = g[g.length - 1] ?? [];
  return { from: g[0]?.[0]?.k ?? scKey(new Date(y, m, 1)), to: last[last.length - 1]?.k ?? scKey(new Date(y, m + 1, 0)) };
}

/** The nominations on the wheel the moment the night starts (the design's `scOnWheel`). */
export function scOnWheel<T extends { from: number | null; to: number | null }>(noms: readonly T[], now: Date): T[] {
  const i = scNowI(now);
  return noms.filter((n) => scFits(n, i));
}

// ---------------------------------------------------------------------------
// wire → model
// ---------------------------------------------------------------------------

const hourOrNull = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v !== '' ? Number(v) : NaN;
  return Number.isFinite(n) && n >= SC_FIRST_HOUR && n <= SC_FIRST_HOUR + SC_NH ? n : null;
};

/** One night's array from `schedule_night_json()` / any schedule RPC. */
export function parseSchedNight(j: unknown): SchedNom[] {
  if (!Array.isArray(j)) return [];
  return j
    .map((x) => {
      const o = (x ?? {}) as Record<string, unknown>;
      const by = typeof o['by'] === 'string' ? o['by'] : '';
      const bysRaw = Array.isArray(o['bys']) ? (o['bys'] as unknown[]).filter((k): k is string => typeof k === 'string') : [];
      const at = typeof o['at'] === 'string' ? Date.parse(o['at']) : typeof o['at'] === 'number' ? o['at'] : 0;
      return {
        id: Number(o['id'] ?? 0),
        by,
        at: Number.isFinite(at) ? at : 0,
        from: hourOrNull(o['from']),
        to: hourOrNull(o['to']),
        // The nominator is always a backer; a row that lost theirs (their
        // profile went) still draws with whoever is left.
        bys: bysRaw.length ? bysRaw : by ? [by] : [],
      };
    })
    .filter((n) => n.id > 0);
}

/** `schedule_get()`'s `{ night: [...] }`. */
export function parseSchedMap(j: unknown): SchedMap {
  const out: SchedMap = {};
  if (!j || typeof j !== 'object' || Array.isArray(j)) return out;
  for (const [k, v] of Object.entries(j as Record<string, unknown>)) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(k)) continue;
    const noms = parseSchedNight(v);
    if (noms.length) out[k] = noms;
  }
  return out;
}
